/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import type { TextFieldPropOnChange } from 'components/FieldNumberFormat';
import { FieldNumberFormat } from 'components/FieldNumberFormat';
import { ControlNumberField } from 'components/Form';
import { DeviationEnum } from 'gql/graphql';
import type { TableColumn, HorizontalAlign, TextFieldPropWidth } from 'uikit';
import { Tooltip, IconAlert } from 'uikit';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';

import { formatNumberToRu } from './formatNumberToRu';

import './tableCells.css';

export interface IGetEditableNumberCellProps {
    title: string | React.ReactNode;
    accessor: string | string[];
    width?: number;
    decimalScale?: number;
    onChange?: TextFieldPropOnChange;
    isEditable?: boolean;
    ValueWrapper?: React.FC<{ row: any; aux?: any; accessor?: any }>;
    wrapperData?: any;
    align?: HorizontalAlign;
    name?: string;
    nameGetter?: (row: any) => string;
    control?: Control<any>;
    doNotCheckRowFlag?: boolean;
    collectionEditAccessor?: string[];
    textFieldWidth?: TextFieldPropWidth;
    rules?: any;
    isTooltipHidden?: boolean;
    placeholder?: string;
    texRight?: boolean;
    inputStyle?: React.CSSProperties;
}

export interface IGetNumberCellProps {
    title: string | React.ReactNode;
    accessor: string | string[];
    width?: number;
    renderCell?: (row: any) => React.ReactNode;
    align?: HorizontalAlign;
    decimal?: number;
}

export const accessorAsName = (accessor: string | string[]) =>
    Array.isArray(accessor) ? accessor.join('.') : accessor;

const getValueByPath = (accessor: string | string[], row: any): any =>
    R.path(Array.isArray(accessor) ? accessor : [accessor], row);

export const formatCellValueToRuNumber =
    (accessor: string | string[], decimal = 2) =>
    (row: any) =>
        formatNumberToRu(getValueByPath(accessor, row), decimal);
export const getNumberCell = (props: IGetNumberCellProps): TableColumn<any> => {
    const { title, accessor, align, width, renderCell, decimal } = props;

    return {
        title,
        accessor: accessorAsName(accessor),
        renderCell: renderCell || formatCellValueToRuNumber(accessor, decimal),
        width,
        align,
    };
};

const getEditableNumberCellValue = (value: any, decimal: number) =>
    value === null ? '—' : formatNumberToRu(value, decimal);

export const getEditableNumberCell = (props: IGetEditableNumberCellProps): TableColumn<any> => {
    const {
        title,
        accessor,
        width,
        decimalScale = 2,
        onChange,
        isEditable,
        ValueWrapper,
        wrapperData,
        align,
        name,
        nameGetter,
        control,
        doNotCheckRowFlag,
        collectionEditAccessor,
        textFieldWidth,
        rules,
        isTooltipHidden,
        placeholder,
        texRight,
        inputStyle,
    } = props;

    const renderCell = (row: any) => {
        const actualName = typeof nameGetter === 'function' ? nameGetter(row) : name || accessorAsName(accessor);
        const editName =
            (collectionEditAccessor?.length || 0) >= 3 && Number.isFinite(row?.index)
                ? accessorAsName([
                      collectionEditAccessor?.[0] || '',
                      String(row?.index) || '',
                      collectionEditAccessor?.[2] || '',
                      collectionEditAccessor?.[3] || '',
                  ])
                : actualName;

        return isEditable && (doNotCheckRowFlag || row?.isEditable) ? (
            control ? (
                <ControlNumberField
                    width={textFieldWidth}
                    {...{ decimalScale }}
                    name={editName}
                    size="s"
                    control={control}
                    rules={rules}
                    isTooltipHidden={isTooltipHidden}
                    placeholder={placeholder}
                    texRight={texRight}
                    inputStyle={inputStyle}
                />
            ) : (
                <FieldNumberFormat
                    decimalScale={decimalScale}
                    value={getValueByPath(accessor, row)}
                    size="s"
                    onChange={onChange}
                    name={actualName}
                    width={textFieldWidth}
                />
            )
        ) : (
            (ValueWrapper && (
                <ValueWrapper row={row} aux={wrapperData} accessor={accessor}>
                    {getEditableNumberCellValue(getValueByPath(accessor, row), decimalScale)}
                </ValueWrapper>
            )) ||
                getEditableNumberCellValue(getValueByPath(accessor, row), decimalScale)
        );
    };

    return getNumberCell({ title, accessor, width, renderCell, align });
};

export const DeviatedCell: React.FC<{ margin?: number }> = (props) => (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <div style={{ color: 'var(--color-typo-alert)', marginRight: props.margin ? `${props.margin}px` : 'auto' }}>
            ▲
        </div>
        <div>{props.children}</div>
    </div>
);

DeviatedCell.displayName = 'DeviatedCell';

interface IDeviatedColumnTitleProps {
    title: string;
    deviation: DeviationEnum | null | undefined;
    tooltipText?: string;
}

export const DeviatedColumnTitle: React.FC<IDeviatedColumnTitleProps> = (props) => {
    const { title, deviation, tooltipText } = props;
    const showAlert = deviation === DeviationEnum.Up;
    const [showTooltip, setShowTooltip] = useState(false);

    const ref = useRef(null);

    const onMouseEnter = useCallback(() => {
        setShowTooltip(true);
    }, []);

    const onMouseLeave = useCallback(() => {
        setShowTooltip(false);
    }, []);

    return (
        <div
            style={{ display: 'flex', alignItems: 'center' }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className="DeviatedColumnTitle"
        >
            {showAlert && (
                <div style={{ marginRight: 8, display: 'flex' }}>
                    <IconAlert ref={ref} size="xs" view="alert" />
                </div>
            )}
            <div>{title}</div>
            {showAlert && tooltipText && showTooltip && (
                <Tooltip size="s" anchorRef={ref}>
                    {tooltipText}
                </Tooltip>
            )}
        </div>
    );
};

DeviatedColumnTitle.displayName = 'WorkOrderPriceActDeviatedColumnTitle';

interface IColoredColumnTitle {
    style?: React.CSSProperties;
    isOdd?: boolean;
    align?: 'center' | 'left' | 'right';
}

export const ColoredColumnTitle: React.FC<IColoredColumnTitle> = (props) => {
    const { children, style, isOdd, align } = props;

    const colorClassName = useMemo(() => (isOdd ? 'coloredColumnTitleOdd' : 'coloredColumnTitleEven'), [isOdd]);

    const alignClassName = useMemo(
        () =>
            align
                ? align === 'center'
                    ? 'coloredColumnTitleCenter'
                    : align === 'left'
                    ? 'coloredColumnTitleLeft'
                    : 'coloredColumnTitleRight'
                : undefined,
        [align],
    );

    return (
        <div className={`coloredColumnTitle ${colorClassName}`} style={style}>
            <div className={alignClassName}>{children}</div>
        </div>
    );
};

ColoredColumnTitle.displayName = 'ColoredColumnTitle';
